import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { FadeUp, MaskedLines } from "@/components/Reveal";
import { ProductCard } from "@/components/ProductCard";
import { useCatalog } from "@/data/catalog";

const CATS = [
  { id: "all", label: "All" },
  { id: "men", label: "Men" },
  { id: "women", label: "Women" },
  { id: "training", label: "Training" },
  { id: "running", label: "Running" },
  { id: "lifestyle", label: "Lifestyle" },
];

const SORTS = [
  { id: "featured", label: "Featured" },
  { id: "price-asc", label: "Price: Low to High" },
  { id: "price-desc", label: "Price: High to Low" },
  { id: "name", label: "Name A–Z" },
];

export default function Shop() {
  const { products: PRODUCTS } = useCatalog();
  const [params, setParams] = useSearchParams();
  const [sort, setSort] = useState("featured");
  const [query, setQuery] = useState("");
  const active = params.get("cat") || params.get("tag") || "all";

  const setFilter = (id) => {
    if (id === "all") setParams({});
    else if (id === "new" || id === "best") setParams({ tag: id });
    else setParams({ cat: id });
  };

  const products = useMemo(() => {
    let list = [...PRODUCTS];
    if (active === "new" || active === "best") list = list.filter((p) => p.tag === active);
    else if (active !== "all") list = list.filter((p) => p.category === active || p.collection === active);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((p) => `${p.name} ${p.collection} ${p.category}`.toLowerCase().includes(q));
    }
    if (sort === "price-asc") list.sort((a, b) => a.price - b.price);
    if (sort === "price-desc") list.sort((a, b) => b.price - a.price);
    if (sort === "name") list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [active, sort, query, PRODUCTS]);

  return (
    <main data-testid="shop-page" className="pt-32 md:pt-40 pb-24">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <p className="text-xs tracking-[0.3em] uppercase text-mist mb-4">MVM — The Full Range</p>
        <MaskedLines
          lines={["SHOP ALL"]}
          className="font-display font-bold uppercase leading-[0.95] tracking-tight text-6xl md:text-8xl"
        />
        <p className="text-stone/55 mt-5 max-w-lg leading-relaxed">
          Built for performance. Designed for everyday. Every piece carries the Me vs Me mindset.
        </p>

        <div className="mt-12 flex flex-col lg:flex-row lg:items-center gap-6 justify-between border-y border-stone/10 py-5">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Categories">
            {[...CATS, { id: "new", label: "New" }, { id: "best", label: "Best Sellers" }].map((c) => (
              <button
                key={c.id}
                data-testid={`shop-filter-${c.id}`}
                onClick={() => setFilter(c.id)}
                className={`font-display uppercase tracking-[0.15em] text-xs px-4 py-2.5 border transition-colors ${
                  active === c.id
                    ? "bg-stone text-obsidian border-stone"
                    : "border-stone/20 text-stone/60 hover:border-stone/60 hover:text-stone"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 border border-stone/20 px-4 py-2.5 flex-1 lg:flex-none">
              <Search size={15} className="text-stone/50" />
              <input
                data-testid="shop-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search products"
                className="bg-transparent text-sm focus:outline-none placeholder:text-stone/35 w-full lg:w-40"
                aria-label="Search products"
              />
            </div>
            <select
              data-testid="shop-sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-obsidian border border-stone/20 text-sm px-4 py-2.5 focus:outline-none focus:border-mist cursor-pointer"
              aria-label="Sort products"
            >
              {SORTS.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <p data-testid="shop-result-count" className="text-xs tracking-[0.25em] uppercase text-stone/40 mt-8">
          {products.length} {products.length === 1 ? "product" : "products"}
        </p>

        {products.length === 0 ? (
          <div data-testid="shop-no-results" className="py-24 text-center text-stone/50">
            Nothing matches that filter. Clear the search or pick another category.
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-10 md:gap-x-6">
            {products.map((p, i) => (
              <FadeUp key={p.id} delay={Math.min(i, 6) * 0.05}>
                <ProductCard product={p} index={i} />
              </FadeUp>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
